import {Injectable} from '@angular/core';
import {
  AutocompleteParams,
  DatasetSearchSchema,
  GlobalSearchSchema,
  IndexService,
  ProjectSearchSchema,
  ResourceSearchSchema,
  SearchDatasetsInIndexParams,
  SearchProjectsAndDatasetsInIndexParams,
  SearchProjectsInIndexParams,
  SearchResourcesInIndexParams,
} from '@middleware';
import {CommonsSearchProject} from '@models/commons-types';
import {HttpClientOptions} from '@models/http';
import {
  IndexSearchMethod,
  IndexSearchResponseTyped,
  PaginatedSearchResults,
  ProjectData,
  SearchBodyMap,
  SearchFilterMap,
  SearchIndexName,
  SearchParamMap,
  SearchReturnMap,
} from '@models/search';
import {TileInfo} from '@models/tile-info';
import {generateTileList} from '@utilities/tiles';
import {lastValueFrom, Observable} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  constructor(private readonly indexService: IndexService) {}

  async getAutocompleteResults(params: AutocompleteParams): Promise<string[]> {
    try {
      return await lastValueFrom(this.indexService.autocomplete(params));
    } catch {
      // Gracefully handle the error by returning an empty list
      return [];
    }
  }

  getIndexSearchMethod<K extends SearchIndexName>(index: K): IndexSearchMethod {
    const s = this.indexService;
    let fn: IndexSearchMethod;

    switch (index) {
      case 'Resources':
        fn = function searchResourcesInIndexThunk(
          resourceSearchSchema: ResourceSearchSchema,
          params: SearchResourcesInIndexParams,
          options: HttpClientOptions,
        ): Observable<IndexSearchResponseTyped<SearchReturnMap[K]>> {
          // Parameter order gets swapped when Orval generates content-type-aware accessors;
          // return type has a weird unpronounceable name for similar reasons.
          return s.searchResourcesInIndex(
            params,
            resourceSearchSchema,
            'application/json',
            options as any,
          ) as any as Observable<IndexSearchResponseTyped<SearchReturnMap[K]>>;
        };
        break;
      case 'Datasets':
        fn = s.searchDatasetsInIndex as (
          datasetSearchSchema: DatasetSearchSchema,
          params: SearchDatasetsInIndexParams,
          options: HttpClientOptions,
        ) => Observable<IndexSearchResponseTyped<SearchReturnMap[K]>>;
        break;
      case 'Projects':
        fn = s.searchProjectsInIndex as (
          projectSearchSchema: ProjectSearchSchema,
          params: SearchProjectsInIndexParams,
          options: HttpClientOptions,
        ) => Observable<IndexSearchResponseTyped<SearchReturnMap[K]>>;
        break;
      case 'ProjectsAndDatasets':
        //@ts-expect-error It's up to caller to pass correct schema.
        fn = s.searchProjectsAndDatasetsInIndex as (
          globalSearchSchema: GlobalSearchSchema,
          params: SearchProjectsAndDatasetsInIndexParams,
          options: HttpClientOptions,
        ) => Observable<IndexSearchResponseTyped<SearchReturnMap[K]>>;
        break;
      default:
        throw new Error(`Invalid index name: ${index}`);
    }
    return fn.bind(this.indexService); // Ensure 'this' context is included within the method
  }

  /** searchGeneric: A generic method to route search calls to various indices.
   *                 Use this to search for multiple objects! */
  async searchGeneric<K extends SearchIndexName>(
    body: SearchBodyMap[K],
    params: SearchParamMap[K],
    index: K,
    page?: number,
    perPage?: number,
  ): Promise<PaginatedSearchResults<SearchReturnMap[K]>> {
    const searchFn = this.getIndexSearchMethod(index);
    const paginatedParams = {...params, page, per_page: perPage};
    const options: HttpClientOptions = {observe: 'body'};
    const raw = await lastValueFrom(searchFn(body, paginatedParams, options));
    const response = raw as unknown as IndexSearchResponseTyped<SearchReturnMap[K]>;

    return {
      results: response.results ?? [],
      hasMore: response.has_more ?? false,
    };
  }

  /** searchByID: A generic method to route search calls to various indices, given an ID.
   *                 Use this to search for singular objects! */
  async searchByID<K extends SearchIndexName>(
    filters: SearchFilterMap[K],
    params: SearchParamMap[K],
    index: K,
  ): Promise<SearchReturnMap[K] | null> {
    const searchFn = this.getIndexSearchMethod(index);
    const options: HttpClientOptions = {observe: 'body'};
    const response = await lastValueFrom(searchFn(filters, params, options));

    if (response && typeof response === 'object' && 'results' in response) {
      const resultsArray = (response as unknown as IndexSearchResponseTyped<SearchReturnMap[K]>).results;

      if (Array.isArray(resultsArray) && resultsArray.length > 0) {
        return resultsArray[0];
      } else {
        return null;
      }
    }

    // Case 2: If it's just an array, unwrap the first item
    if (Array.isArray(response) && response.length > 0) {
      return response[0] as SearchReturnMap[K];
    }

    // Case 3: If it's its own object, return that
    if (response && typeof response === 'object') {
      return response as unknown as SearchReturnMap[K];
    }

    return null;
  }

  /** loadProjectDetails: Loads a public project and all dependencies.  */
  async loadProjectDetails(projectID: string, public_view: boolean = true): Promise<ProjectData> {
    const projectSearchParams: SearchProjectsInIndexParams = {
      public_view: public_view,
    };
    const projectSearchBody: ProjectSearchSchema = {
      id: {terms: projectID},
    };

    const datasetSearchParams: SearchDatasetsInIndexParams = {
      public_view: true,
    };
    const datasetSearchBody: DatasetSearchSchema = {
      associated_projects: {terms: projectID},
    };

    const projectResp: CommonsSearchProject = await this.searchByID(projectSearchBody, projectSearchParams, 'Projects');

    const {results: datasetResults} = await this.searchGeneric(
      datasetSearchBody,
      datasetSearchParams,
      'Datasets',
      1,
      100,
    );

    const datasetTiles = generateTileList(datasetResults);

    const resourceIds = projectResp.associated_resource_ids ?? [];
    const resourceTiles = await this.buildResourceTileList(resourceIds);

    return {
      project: projectResp,
      resourceTiles,
      datasetTiles,
    };
  }

  /** buildResourceTileList: A helper method that creates a list of tiles for a project's related resources.  */
  async buildResourceTileList(resourceIDs: string[]): Promise<TileInfo[]> {
    if (!resourceIDs.length) return [];

    const resourceParams = {public_view: true};
    const resourceBody = {id: {terms: resourceIDs}};

    try {
      const {results} = await this.searchGeneric(resourceBody, resourceParams, 'Resources', 1, resourceIDs.length);

      const idSet = new Set(resourceIDs);

      return generateTileList(results.filter(r => r.id && idSet.has(r.id)));
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      return [];
    }
  }
}
